import { useCallback, useRef, useEffect, RefObject } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

interface UseResizeObserverParams<T extends HTMLElement> {
    ref: RefObject<T>;
    onResize?: (rect: DOMRect) => void;
    /** Default: false */
    enabled?: boolean;
}

let ResizeObserverImplementation = ResizeObserver;

if (typeof window === 'undefined') {
    ResizeObserverImplementation = ResizeObserver;
}

if (typeof window !== 'undefined' && window.ResizeObserver) {
    ResizeObserverImplementation = window.ResizeObserver;
}

export const useResizeObserver = <T extends HTMLElement>(params: UseResizeObserverParams<T>) => {
    const { ref, enabled = false, onResize } = params;

    const handleResize = useCallback(
        (entries: ResizeObserverEntry[]) => {
            if (entries[0]) {
                const elem = entries[0].target;
                const rect = elem.getBoundingClientRect();
                onResize?.(rect);
            }
        },
        [onResize]
    );

    const observerRef = useRef<ResizeObserver | null>(null);

    useEffect(() => {
        const elem = ref.current;
        if (!elem || !enabled) {
            return;
        }

        observerRef.current = new ResizeObserverImplementation(handleResize);
        if (observerRef.current) {
            observerRef.current.observe(elem);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, handleResize]);
};
